import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, Typography } from '@mui/material';

import SimpleTextInput from '../Components/SimpleTextInput';
import KebabInput from '../Components/KebabInput';
import MRNInput from '../Components/MRNInput';
import RightLink from '../Components/RightLink';
import ReadOnlyInput from '../Components/ReadOnlyInput';
import NeenorAutocomplete from '../Components/NeenorAutocomplete';
import { ConsignmentDetailsModal, InvalidMRNModal } from '../Modals/ConsignmentModals';
import { PlaceOfLoadingModal, PlaceOfUnloadingModal } from '../Modals/TransportAndJourneyModals';
import { GBENSContext } from '../GBENSContainer';

import { useStepper } from '../../../components/Stepper';
import { getMRNDetails } from '../../../api/GBENSDec';

const AddingConsignments = ({ handleGetMRNDetails }) => {
  const [consignmentDetailsModalOpen, setConsignmentDetailsModalOpen] = useState(false);
  const [placeOfLoadingModalOpen, setPlaceOfLoadingModalOpen] = useState(false);
  const [placeOfUnloadingModalOpen, setPlaceOfUnloadingModalOpen] = useState(false);
  const [invalidMRNModalOpen, setInvalidMRNModalOpen] = useState(false);

  const screen = 'Consignments';

  const {
    currentConsignment,
    data,
    handleShowWarningModal,
    updateCurrentConsignment,
    addNewConsignmentLine,
    editConsignmentLine,
    isReadOnly,
  } = useContext(GBENSContext);

  const { gotoStep } = useStepper();

  const clickEditLine = (id) => {
    if (!currentConsignment || !Array.isArray(currentConsignment.lines) || currentConsignment.lines.length === 0) {
      clickNewLine();
    } else {
      editConsignmentLine(id);
      setConsignmentDetailsModalOpen(true);
    }
  };

  const clickNewLine = () => {
    addNewConsignmentLine();
    setConsignmentDetailsModalOpen(true);
  };

  const onNext = async () => {
    if (isReadOnly) {
      gotoStep('Consignments');
    } else {
      handleShowWarningModal('AddingConsignment', () => gotoStep('Consignments'));
    }
  };

  const handleMRNClick = async (inputValue) => {
    try {
      if (!inputValue) {
        updateCurrentConsignment({ target: { value: '' } }, 'cabieImportMrn');
        return;
      }
      const data = await getMRNDetails(inputValue);
      updateCurrentConsignment({ target: { value: inputValue } }, 'cabieImportMrn');
      handleGetMRNDetails(data);
    } catch (err) {
      console.log(err);
      setInvalidMRNModalOpen(true);
    }
  };

  const TJPlaceOfLoadingPresent = useMemo(() => {
    const country = data?.TransportAndJourney?.PlaceOfLoading?.country;
    const countrySelected = country && country !== -1 && country?.trim?.() !== '';
    const placeName =
      data?.TransportAndJourney?.PlaceOfLoading?.placeName &&
      data?.TransportAndJourney?.PlaceOfLoading?.placeName?.trim?.()?.length > 0;
    const postcode =
      data?.TransportAndJourney?.PlaceOfLoading?.postcode &&
      data?.TransportAndJourney?.PlaceOfLoading?.postcode?.trim?.()?.length > 0;
    return !!(countrySelected || placeName || postcode);
  }, [data]);

  const TJPlaceOfUnloadingPresent = useMemo(() => {
    const country = data?.TransportAndJourney?.PlaceOfUnloading?.country;
    const countrySelected = country && country !== -1 && country?.trim?.() !== '';
    const placeName =
      data?.TransportAndJourney?.PlaceOfUnloading?.placeName &&
      data?.TransportAndJourney?.PlaceOfUnloading?.placeName?.trim?.()?.length > 0;
    const postcode =
      data?.TransportAndJourney?.PlaceOfUnloading?.postcode &&
      data?.TransportAndJourney?.PlaceOfUnloading?.postcode?.trim?.()?.length > 0;
    return !!(countrySelected || placeName || postcode);
  }, [data]);

  return (
    <>
      <ConsignmentDetailsModal open={consignmentDetailsModalOpen} setOpen={setConsignmentDetailsModalOpen} />
      <PlaceOfLoadingModal isConsignment open={placeOfLoadingModalOpen} setOpen={setPlaceOfLoadingModalOpen} />
      <PlaceOfUnloadingModal isConsignment open={placeOfUnloadingModalOpen} setOpen={setPlaceOfUnloadingModalOpen} />
      <InvalidMRNModal open={invalidMRNModalOpen} setOpen={setInvalidMRNModalOpen} />

      <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
        <div style={{ marginBottom: 30 }}>
          <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 30 }}>
            An entry summary declaration must be submitted for every <b>individual</b> consignment.
          </Typography>
          <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 30 }}>
            Using the &apos;Add Additional Consignment&apos; button will allow you to add each consignment the trailer
            or container may be carrying. Each consignment will receive an MRN to allow the goods to be imported into
            Great Britain.
          </Typography>
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 5 }}>
          <Typography id="modal-modal-title" variant="h5" component="h2" style={{ marginLeft: 30, fontWeight: 'bold' }}>
            EORI Customer?
          </Typography>
          <Typography
            id="modal-modal-title"
            variant="p"
            component="p"
            style={{ marginLeft: 10, alignItems: 'baseline', marginTop: 8 }}
          >
            If you have an MRN, enter it now to link add the consignment.
          </Typography>
        </div>

        <MRNInput
          name="CABIE Frontier MRN:"
          consignment
          id="MRN"
          onClick={(inputValue) => handleMRNClick(inputValue)}
        />

        <div style={{ display: 'flex', flexDirection: 'row', marginTop: 15, marginBottom: 15 }}>
          <Typography id="modal-modal-title" variant="h5" component="h2" style={{ marginLeft: 30, fontWeight: 'bold' }}>
            Consignment Header
          </Typography>
        </div>

        <SimpleTextInput
          name="Consignment Reference:"
          consignment
          placeholder="Your reference / Dispatch note / Invoice number / etc"
          id="consignmentReference"
          screen={screen}
          disabled={isReadOnly}
        />

        <NeenorAutocomplete
          name="Consignor Details:"
          modal="Consignor"
          placeholder="Type to find, or add new consignor details"
          id="Consignor"
          disabled={isReadOnly}
        />

        <NeenorAutocomplete
          name="Consignee Details:"
          modal="Consignee"
          placeholder="Type to find, or add new consignee details"
          id="Consignee"
          disabled={isReadOnly}
        />

        {TJPlaceOfLoadingPresent ? (
          <ReadOnlyInput value="Haulier's Location" name="Place of Loading:" />
        ) : (
          <KebabInput
            name="Place of Loading:"
            value={
              currentConsignment?.PlaceOfLoading?.placeName &&
              currentConsignment?.PlaceOfLoading?.placeName?.trim?.()?.length > 0
                ? currentConsignment?.PlaceOfLoading?.placeName
                : ''
            }
            placeholder="This will default to the consignors address if left blank"
            onClick={() => setPlaceOfLoadingModalOpen(true)}
            consignmentKebab
            id="PlaceOfLoading"
            screen={screen}
            disabled={isReadOnly}
          />
        )}

        {TJPlaceOfUnloadingPresent ? (
          <ReadOnlyInput value="Haulier's Location" name="Place of Unloading:" />
        ) : (
          <KebabInput
            name="Place of Unloading:"
            value={
              currentConsignment?.PlaceOfUnloading?.placeName &&
              currentConsignment?.PlaceOfUnloading?.placeName?.trim?.()?.length > 0
                ? currentConsignment?.PlaceOfUnloading?.placeName
                : ''
            }
            placeholder="This will default to the consignees address if left blank"
            onClick={() => setPlaceOfUnloadingModalOpen(true)}
            consignmentKebab
            id="PlaceOfUnloading"
            screen={screen}
            disabled={isReadOnly}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'row', marginTop: 15, marginBottom: 5 }}>
          <Typography id="modal-modal-title" variant="h5" component="h2" style={{ marginLeft: 30, fontWeight: 'bold' }}>
            Consignment Details
          </Typography>
        </div>
        {(currentConsignment?.lines?.length ? currentConsignment.lines : [{ id: 0, goodsDescription: '' }])?.map?.(
          (c, i) => {
            return (
              <KebabInput
                key={i}
                name={`Line ${i + 1}:`}
                value={c.goodsDescription}
                placeholder="Click link to add"
                onClick={() => clickEditLine(c.id)}
                consignmentLine
                screen={screen}
                id={c.id}
                disabled={isReadOnly}
              />
            );
          }
        )}
        <RightLink name="Add Additional Line" onClick={clickNewLine} disabled={isReadOnly} />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: 30,
          paddingLeft: 30,
          paddingRight: 30,
        }}
      >
        <div></div>
        <Button variant="outlined" onClick={onNext} disabled={isReadOnly}>
          Save Consignment
        </Button>
      </div>
    </>
  );
};

AddingConsignments.propTypes = {
  handleGetMRNDetails: PropTypes.func.isRequired,
};

export default AddingConsignments;
